import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import api from "@/services/api";
import { Link, useNavigate } from "react-router-dom";
import {
  HiOutlineDocumentText,
  HiOutlineAcademicCap,
  HiOutlineSparkles,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineChartBar,
  HiOutlineFire,
  HiOutlineBookOpen,
  HiOutlineTrophy,
  HiOutlineArrowRight,
  HiOutlineUserGroup,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlineRocketLaunch,
  HiOutlineStar,
  HiOutlineClipboardDocumentCheck,
  HiOutlineCalendarDays,
  HiOutlinePlayCircle,
} from "react-icons/hi2";
import placeholder from "@/assets/learn/placeholder.png";
import { isInSelesquad } from "@/utils/roles";

const Home = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.Auth.user);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const isSelesquad = isInSelesquad(user);
  const isTrainee = user?.training_status === "in_training" || user?.training_status === "ready_for_evaluation";

  const [stats, setStats] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [recommendedWorkshops, setRecommendedWorkshops] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(true);

  // Admin-specific state
  const [adminStats, setAdminStats] = useState(null);
  const [loadingAdmin, setLoadingAdmin] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [trainingPipeline, setTrainingPipeline] = useState(null);
  const [topWorkshops, setTopWorkshops] = useState([]);

  // Trainee-specific state
  const [recentUserWorkshops, setRecentUserWorkshops] = useState([]);
  const [loadingTrainee, setLoadingTrainee] = useState(true);
  const [careerPaths, setCareerPaths] = useState([]);
  const [userWorkshopRecords, setUserWorkshopRecords] = useState([]);
  const [careerPathWorkshops, setCareerPathWorkshops] = useState({}); // { courseId: [workshops] }

  useEffect(() => {
    fetchUserStats();
    fetchLatestWorkshops();
    fetchCourses();
    fetchRecommendedContent();
    if ((isAdmin || isSelesquad) && !isTrainee) {
      fetchAdminData();
    }
    if (isTrainee) {
      fetchTraineeData();
    }
  }, []);

  async function fetchUserStats() {
    try {
      const { data } = await api.post("/workshop_user/contribution");
      setStats(data || {});
    } catch (e) {
      setStats({});
    }
  }

  async function fetchLatestWorkshops() {
    setLoading(true);
    try {
      const { data } = await api.post("/workshop/search", {
        perPage: 3,
        page: 1,
        short: true,
        sort: "rating",
      });
      setWorkshops(Array.isArray(data) ? data.slice(0, 3) : []);
    } catch (e) {
      setWorkshops([]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchCourses() {
    setLoadingCourses(true);
    try {
      const { data, ok } = await api.post("/training/search", { perPage: 4, page: 1 });
      if (!ok) throw new Error();
      setCourses(Array.isArray(data) ? data.slice(0, 4) : []);
    } catch (e) {
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  }

  async function fetchRecommendedContent() {
    setLoadingRecommended(true);
    try {
      const { data } = await api.post("/workshop/search", {
        perPage: 6,
        page: 1,
        short: true,
        sort: "rating",
      });
      setRecommendedWorkshops(Array.isArray(data) ? data.slice(0, 6) : []);
    } catch (e) {
      setRecommendedWorkshops([]);
    } finally {
      setLoadingRecommended(false);
    }
  }

  async function fetchAdminData() {
    setLoadingAdmin(true);
    try {
      const [recentRes, topContribRes, workshopRes, inTrainingRes, readyRes, graduatedRes] = await Promise.all([
        api.post("/workshop_user/search", { per_page: 8, page: 1 }),
        api.post("/workshop_user/top_contributors", { period: "month" }),
        api.post("/workshop/search", { perPage: 1000, short: true }),
        api.post("/user/search", { limit: 1000, training_status: "in_training" }),
        api.post("/user/search", { limit: 1000, training_status: "ready_for_evaluation" }),
        api.post("/user/search", { limit: 1000, training_status: "graduated" }),
      ]);

      if (recentRes.ok) setRecentActivity(recentRes.data || []);

      const allWorkshops = workshopRes.ok ? workshopRes.data || [] : [];
      const totalCompletions = recentRes.total || 0;

      setTopWorkshops(allWorkshops.slice(0, 5));

      const inTraining = inTrainingRes.ok ? inTrainingRes.data?.users || [] : [];
      const readyForEval = readyRes.ok ? readyRes.data?.users || [] : [];
      const graduated = graduatedRes.ok ? graduatedRes.data?.users || [] : [];

      setTrainingPipeline({
        in_training: inTraining,
        ready_for_evaluation: readyForEval,
        graduated: graduated,
      });

      setAdminStats({
        totalWorkshops: allWorkshops.length,
        totalCompletions,
        totalContributors: topContribRes.ok ? topContribRes.data?.length || 0 : 0,
        topContributors: topContribRes.ok ? topContribRes.data?.slice(0, 5) || [] : [],
        trainingTotal: inTraining.length + readyForEval.length + graduated.length,
      });
    } catch (e) {
      console.error("Error fetching admin data:", e);
    } finally {
      setLoadingAdmin(false);
    }
  }

  async function fetchTraineeData() {
    setLoadingTrainee(true);
    try {
      // Fetch career paths, user's workshop records, and recent activity in parallel
      const [careerRes, userWsRes] = await Promise.all([
        api.post("/career_path/search", {}),
        api.get("/workshop_user"),
      ]);

      const paths = careerRes.ok ? careerRes.data || [] : [];
      setCareerPaths(paths);
      setUserWorkshopRecords(userWsRes.ok ? userWsRes.data || [] : []);
      setRecentUserWorkshops(userWsRes.ok ? userWsRes.data || [] : []);

      // Fetch workshops for all courses across all career paths
      const allCourseIds = [];
      paths.forEach((path) => {
        (path.trainings || []).forEach((t) => {
          if (t.training_id && !allCourseIds.includes(t.training_id)) {
            allCourseIds.push(t.training_id);
          }
        });
      });

      // Batch fetch workshops for each course
      const workshopMap = {};
      await Promise.all(
        allCourseIds.map(async (courseId) => {
          try {
            const res = await api.post("/training_workshop/search", { training_id: courseId });
            if (res.ok) workshopMap[courseId] = res.data || [];
          } catch {
            workshopMap[courseId] = [];
          }
        }),
      );
      setCareerPathWorkshops(workshopMap);
    } catch (e) {
      console.error("Error fetching trainee data:", e);
      setRecentUserWorkshops([]);
    } finally {
      setLoadingTrainee(false);
    }
  }

  const completionRate = stats?.workshops_total && stats?.workshops_completed ? Math.round((stats.workshops_completed / stats.workshops_total) * 100) : 0;
  const daysInTraining = user?.training_start_date ? Math.floor((new Date() - new Date(user.training_start_date)) / (1000 * 60 * 60 * 24)) + 1 : 0;

  // Compute per-course progress for trainee
  const getCourseProgress = (courseId) => {
    const courseWorkshops = careerPathWorkshops[courseId] || [];
    if (courseWorkshops.length === 0) return { total: 0, completed: 0, inProgress: 0, percent: 0, nextWorkshop: null };
    const completed = courseWorkshops.filter((w) => userWorkshopRecords.some((uw) => uw.workshop_id === w.workshop_id && uw.finished_at)).length;
    const inProgress = courseWorkshops.filter((w) => userWorkshopRecords.some((uw) => uw.workshop_id === w.workshop_id && !uw.finished_at)).length;
    const nextWorkshop = courseWorkshops.find(
      (w) => !userWorkshopRecords.some((uw) => uw.workshop_id === w.workshop_id && uw.finished_at),
    );
    return { total: courseWorkshops.length, completed, inProgress, percent: Math.round((completed / courseWorkshops.length) * 100), nextWorkshop };
  };

  // Find the overall next action for the trainee
  const getNextAction = () => {
    for (const path of careerPaths) {
      const sortedTrainings = (path.trainings || []).slice().sort((a, b) => (a.training_position || 0) - (b.training_position || 0));
      for (const training of sortedTrainings) {
        const progress = getCourseProgress(training.training_id);
        if (progress.percent < 100 && progress.nextWorkshop) {
          const uw = userWorkshopRecords.find((u) => u.workshop_id === progress.nextWorkshop.workshop_id && !u.finished_at);
          return {
            type: "workshop",
            pathName: path.name,
            pathId: path._id,
            courseName: training.training_title,
            courseId: training.training_id,
            workshopName: progress.nextWorkshop.workshop_name,
            workshopId: progress.nextWorkshop.workshop_id,
            stepId: uw?.step_id || null,
            courseProgress: progress,
          };
        }
      }
    }
    return null;
  };

  // ═══════════════════ TRAINEE VIEW ═══════════════════
  if (isTrainee) {
    const nextAction = !loadingTrainee ? getNextAction() : null;

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* ── Welcome + Status Row ── */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold mb-1">Welcome back, {user.name?.split(" ")[0]}!</h1>
                <p className="text-blue-100 text-sm">Your training journey at Selego</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-white/15 rounded-xl px-4 py-2 backdrop-blur-sm">
                  <HiOutlineCalendarDays className="w-5 h-5" />
                  <div>
                    <div className="text-lg font-bold">Day {daysInTraining || 1}</div>
                    <div className="text-xs text-blue-200">of training</div>
                  </div>
                </div>
                {user.training_phase && (
                  <div className="bg-white/15 rounded-xl px-4 py-2 backdrop-blur-sm">
                    <div className="text-xs text-blue-200 mb-1">Phase</div>
                    <div className="text-sm font-bold capitalize">{user.training_phase}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Training Phase Progress */}
            {user.training_phase && (
              <div className="mt-4">
                <div className="flex gap-1">
                  {["onboarding", "learning", "practicing", "ready"].map((phase) => {
                    const phases = ["onboarding", "learning", "practicing", "ready"];
                    const isCurrent = phase === user.training_phase;
                    const isPast = phases.indexOf(phase) < phases.indexOf(user.training_phase);
                    return (
                      <div key={phase} className="flex-1 flex flex-col items-center gap-1">
                        <div className={`w-full h-1.5 rounded-full ${isCurrent ? "bg-white" : isPast ? "bg-white/50" : "bg-white/15"}`} />
                        <span className={`text-[10px] capitalize ${isCurrent ? "text-white font-semibold" : "text-white/50"}`}>{phase}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── What To Do Next (Primary CTA) ── */}
          {!loadingTrainee && nextAction && (
            <div className="bg-white rounded-2xl shadow-sm border-2 border-blue-200 p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">What to do next</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="text-xs text-gray-500 mb-1">
                    {nextAction.pathName} → {nextAction.courseName}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{nextAction.workshopName}</h3>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span>{nextAction.courseProgress.completed}/{nextAction.courseProgress.total} workshops done in this course</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const params = new URLSearchParams();
                    params.set("courseId", nextAction.courseId);
                    params.set("pathId", nextAction.pathId);
                    if (nextAction.stepId) params.set("index", nextAction.stepId);
                    navigate(`/selecole/learn/${nextAction.workshopId}?${params.toString()}`);
                  }}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-xl transition-colors duration-200 shadow-md flex-shrink-0">
                  {nextAction.stepId ? "Continue" : "Start"} <HiOutlineArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Your Learning Paths ── */}
          {!loadingTrainee && careerPaths.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <HiOutlineSparkles className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Your Learning Paths</h2>
                  <p className="text-xs text-gray-500">Follow these paths to complete your training</p>
                </div>
              </div>

              {careerPaths.map((path) => {
                const sortedTrainings = (path.trainings || []).slice().sort((a, b) => (a.training_position || 0) - (b.training_position || 0));
                const totalCourseWorkshops = sortedTrainings.reduce((sum, t) => sum + (careerPathWorkshops[t.training_id]?.length || 0), 0);
                const completedCourseWorkshops = sortedTrainings.reduce((sum, t) => {
                  const ws = careerPathWorkshops[t.training_id] || [];
                  return sum + ws.filter((w) => userWorkshopRecords.some((uw) => uw.workshop_id === w.workshop_id && uw.finished_at)).length;
                }, 0);
                const pathPercent = totalCourseWorkshops > 0 ? Math.round((completedCourseWorkshops / totalCourseWorkshops) * 100) : 0;

                return (
                  <div key={path._id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {path.image && <img src={path.image} className="w-10 h-10 rounded-lg object-cover" alt={path.name} />}
                        <div>
                          <h3 className="font-bold text-gray-900">{path.name}</h3>
                          <p className="text-xs text-gray-500">{sortedTrainings.length} courses · {totalCourseWorkshops} workshops</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${pathPercent === 100 ? "text-green-600" : "text-blue-600"}`}>{pathPercent}%</span>
                        <Link to={`/selecole/career-path/${path._id}`} className="text-xs text-gray-500 hover:text-blue-600">
                          View →
                        </Link>
                      </div>
                    </div>

                    {/* Path progress bar */}
                    <div className="w-full bg-gray-100 rounded-full h-2 mb-5">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${pathPercent === 100 ? "bg-green-500" : "bg-blue-500"}`}
                        style={{ width: `${pathPercent}%` }}
                      />
                    </div>

                    {/* Course list */}
                    <div className="space-y-2">
                      {sortedTrainings.map((training, i) => {
                        const progress = getCourseProgress(training.training_id);
                        const isComplete = progress.percent === 100;
                        const hasStarted = progress.completed > 0 || progress.inProgress > 0;

                        return (
                          <div
                            key={training.training_id}
                            onClick={() => {
                              const params = new URLSearchParams();
                              params.set("pathId", path._id);
                              navigate(`/selecole/course/${training.training_id}?${params.toString()}`);
                            }}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 group
                              ${isComplete ? "bg-green-50 hover:bg-green-100" : hasStarted ? "bg-blue-50 hover:bg-blue-100" : "bg-gray-50 hover:bg-gray-100"}`}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
                              ${isComplete ? "bg-green-500 text-white" : hasStarted ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-500"}`}>
                              {isComplete ? <HiOutlineCheckCircle className="w-4 h-4" /> : i + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900 truncate">{training.training_title}</span>
                                {isComplete && <span className="text-[10px] font-medium text-green-600 bg-green-100 px-1.5 py-0.5 rounded-full">Done</span>}
                                {hasStarted && !isComplete && (
                                  <span className="text-[10px] font-medium text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full">
                                    {progress.completed}/{progress.total}
                                  </span>
                                )}
                              </div>
                              {progress.total > 0 && (
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex-1 bg-gray-200 rounded-full h-1 max-w-[120px]">
                                    <div
                                      className={`h-1 rounded-full ${isComplete ? "bg-green-500" : "bg-blue-500"}`}
                                      style={{ width: `${progress.percent}%` }}
                                    />
                                  </div>
                                  <span className="text-[10px] text-gray-400">{progress.total} workshops</span>
                                </div>
                              )}
                            </div>
                            <HiOutlineArrowRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Training Test CTA ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <HiOutlineClipboardDocumentCheck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Training Evaluation</h3>
                <p className="text-xs text-gray-500">Take the test when you feel ready</p>
              </div>
            </div>

            {user.training_test_completed_at ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-600">
                  <HiOutlineCheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Test Completed</span>
                </div>
                {user.training_test_results && (
                  <div className="bg-green-50 rounded-xl p-3">
                    <div className="grid grid-cols-2 gap-3 text-center">
                      <div>
                        <div className="text-xl font-bold text-green-700">
                          {user.training_test_results.correct}/{user.training_test_results.totalQuestions}
                        </div>
                        <div className="text-xs text-green-600">Correct</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-green-700">
                          {Math.round((user.training_test_results.correct / user.training_test_results.totalQuestions) * 100)}%
                        </div>
                        <div className="text-xs text-green-600">Score</div>
                      </div>
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-500">Your manager will review your results.</p>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-600 flex-1">
                  When you've completed the learning paths above, take the evaluation test to demonstrate what you've learned.
                </p>
                <button
                  onClick={() => navigate(`/selecole/training-test/${user._id}`)}
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-medium py-2.5 px-5 rounded-xl transition-colors duration-200 shadow-sm text-sm flex-shrink-0">
                  <HiOutlinePlayCircle className="w-5 h-5" />
                  Take the Test
                </button>
              </div>
            )}
          </div>

          {/* ── Recent Activity ── */}
          {!loadingTrainee && recentUserWorkshops.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center">
                    <HiOutlineClock className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="font-bold text-gray-900">Your Recent Activity</h3>
                </div>
                <Link to="/selecole/learn" className="text-xs text-gray-500 hover:text-blue-600">
                  See all →
                </Link>
              </div>
              <div className="space-y-1">
                {recentUserWorkshops.slice(0, 5).map((item) => (
                  <div
                    key={item._id}
                    className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/selecole/learn/${item.workshop_id}`)}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      item.finished_at ? "bg-green-100" : "bg-blue-100"
                    }`}>
                      {item.finished_at
                        ? <HiOutlineCheckCircle className="w-4 h-4 text-green-600" />
                        : <HiOutlinePlayCircle className="w-4 h-4 text-blue-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.workshop_name}</p>
                      <p className="text-xs text-gray-500">
                        {item.finished_at ? "Completed" : "In progress"} · {new Date(item.updated_at || item.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {item.feedback_rating && (
                      <span className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                        <HiOutlineStar className="w-3 h-3" />
                        {item.feedback_rating}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════ ADMIN / REGULAR USER VIEW ═══════════════════
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* ═══════════════════ ADMIN DASHBOARD ═══════════════════ */}
        {(isAdmin || isSelesquad) && !loadingAdmin && adminStats && (
          <>
            {/* School Health KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPICard
                icon={<HiOutlineBookOpen className="w-5 h-5" />}
                label="Total Workshops"
                value={adminStats.totalWorkshops}
                color="blue"
                onClick={() => navigate("/selecole/learn")}
              />
              <KPICard
                icon={<HiOutlineCheckCircle className="w-5 h-5" />}
                label="Completions"
                value={adminStats.totalCompletions}
                subtitle="all time"
                color="green"
                onClick={() => navigate("/selecole/rating")}
              />
              <KPICard
                icon={<HiOutlineUserGroup className="w-5 h-5" />}
                label="Active Contributors"
                value={adminStats.totalContributors}
                subtitle="this month"
                color="purple"
                onClick={() => navigate("/selecole/stats")}
              />
              <KPICard
                icon={<HiOutlineRocketLaunch className="w-5 h-5" />}
                label="In Training Pipeline"
                value={adminStats.trainingTotal}
                subtitle="people"
                color="orange"
                onClick={() => navigate("/selecole/tracker")}
              />
            </div>

            {/* Training Pipeline + Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Training Pipeline */}
              {trainingPipeline && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 bg-indigo-100 rounded-xl flex items-center justify-center">
                        <HiOutlineRocketLaunch className="w-5 h-5 text-indigo-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Training Pipeline</h3>
                    </div>
                    <Link to="/selecole/tracker/in-training" className="text-xs font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                      View all <HiOutlineArrowRight className="w-3 h-3" />
                    </Link>
                  </div>

                  {/* Pipeline Funnel */}
                  <div className="space-y-3">
                    <PipelineStage
                      label="In Training"
                      count={trainingPipeline.in_training.length}
                      color="bg-blue-500"
                      lightColor="bg-blue-50 text-blue-700"
                      total={adminStats.trainingTotal}
                      users={trainingPipeline.in_training.slice(0, 4)}
                      to="/selecole/tracker/in-training"
                    />
                    <PipelineStage
                      label="Ready for Eval"
                      count={trainingPipeline.ready_for_evaluation.length}
                      color="bg-purple-500"
                      lightColor="bg-purple-50 text-purple-700"
                      total={adminStats.trainingTotal}
                      users={trainingPipeline.ready_for_evaluation.slice(0, 4)}
                      to="/selecole/tracker/ready-for-evaluation"
                    />
                    <PipelineStage
                      label="Graduated"
                      count={trainingPipeline.graduated.length}
                      color="bg-green-500"
                      lightColor="bg-green-50 text-green-700"
                      total={adminStats.trainingTotal}
                      users={trainingPipeline.graduated.slice(0, 4)}
                      to="/selecole/tracker/graduated"
                    />
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center">
                      <HiOutlineClock className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Recent Activity</h3>
                  </div>
                  <Link to="/selecole/rating" className="text-xs font-medium text-green-600 hover:text-green-700 flex items-center gap-1">
                    View all <HiOutlineArrowRight className="w-3 h-3" />
                  </Link>
                </div>

                {recentActivity.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No recent activity</p>
                ) : (
                  <div className="space-y-1">
                    {recentActivity.slice(0, 8).map((item) => (
                      <div
                        key={item._id}
                        className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/selecole/learn/${item.workshop_id}`)}>
                        <img src={item.user_avatar} alt={item.user_name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">{item.user_name}</span>
                            <span className="text-xs text-gray-400">
                              {item.finished_at ? "completed" : "started"}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">{item.workshop_name}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {item.feedback_rating ? (
                            <span className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                              <HiOutlineStar className="w-3 h-3" />
                              {item.feedback_rating}
                            </span>
                          ) : null}
                          <span className="text-xs text-gray-400">{item.created_at ? new Date(item.created_at).toLocaleDateString() : ""}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Top Contributors This Month */}
            {adminStats.topContributors.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center">
                      <HiOutlineTrophy className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Top Contributors</h3>
                      <p className="text-xs text-gray-500">This month's most active learners</p>
                    </div>
                  </div>
                  <Link to="/selecole/stats" className="text-xs font-medium text-amber-600 hover:text-amber-700 flex items-center gap-1">
                    View all <HiOutlineArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {adminStats.topContributors.map((contributor, i) => (
                    <div key={contributor._id || i} className="flex flex-col items-center text-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="relative mb-2">
                        <img src={contributor.user_avatar} alt={contributor.user_name} className="w-12 h-12 rounded-full object-cover" />
                        {i < 3 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 text-white text-xs font-bold flex items-center justify-center shadow">
                            {i + 1}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-900 truncate w-full">{contributor.user_name}</span>
                      <span className="text-xs text-gray-500">{contributor.countWorkshops} workshops</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Divider between admin and user content */}
            <div className="flex items-center gap-4 pt-2">
              <div className="flex-1 border-t border-gray-200" />
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Learning Content</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>
          </>
        )}

        {/* ═══════════════════ USER CONTENT (existing) ═══════════════════ */}

        {/* Personal Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={<HiOutlineBookOpen className="w-6 h-6" />}
            title="Workshops Completed"
            value={stats?.workshops_completed || 0}
            subtitle={`${stats?.workshops_total || 0} total available`}
            color="blue"
            onClick={() => navigate("/selecole/learn")}
          />
          <StatCard
            icon={<HiOutlineTrophy className="w-6 h-6" />}
            title="Workshops Started"
            value={stats?.workshops_started || 0}
            subtitle={`${(stats?.workshops_started || 0) - (stats?.workshops_completed || 0)} in progress`}
            color="purple"
            onClick={() => navigate("/selecole/learn")}
          />
          <StatCard
            icon={<HiOutlineChartBar className="w-6 h-6" />}
            title="Completion Rate"
            value={`${completionRate}%`}
            subtitle={completionRate >= 50 ? "Great progress!" : "Keep going!"}
            color="green"
            progress={completionRate}
          />
          <StatCard
            icon={<HiOutlineFire className="w-6 h-6" />}
            title="Total Workshops"
            value={stats?.workshops_total || 0}
            subtitle="Available to explore"
            color="orange"
            onClick={() => navigate("/selecole/learn")}
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions & Resources */}
          <div className="lg:col-span-1 space-y-6">
            <QuickActionsCard />
            <ResourcesCard />
          </div>

          {/* Featured Workshops */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <HiOutlineSparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Featured Workshops</h3>
                    <p className="text-gray-600 text-sm">Hand-picked workshops to get you started</p>
                  </div>
                </div>
                <Link to="/selecole/learn" className="text-sm font-medium text-purple-600 hover:text-purple-700 flex items-center gap-1">
                  View all <HiOutlineArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {loading
                  ? Array.from({ length: 2 }).map((_, index) => <WorkshopSkeleton key={index} />)
                  : workshops.map((workshop) => <WorkshopCard key={workshop._id} workshop={workshop} onClick={() => navigate(`/selecole/learn/${workshop._id}`)} />)}
              </div>
            </div>
          </div>
        </div>

        {/* Recommended Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                <HiOutlineAcademicCap className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Recommended for You</h3>
                <p className="text-gray-600">Based on your learning journey</p>
              </div>
            </div>
            <Link to="/selecole/learn" className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Explore more <HiOutlineArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {loadingRecommended
              ? Array.from({ length: 4 }).map((_, index) => <CourseSkeleton key={index} />)
              : recommendedWorkshops.map((workshop) => <WorkshopCard key={workshop._id} workshop={workshop} onClick={() => navigate(`/selecole/learn/${workshop._id}`)} compact />)}
          </div>
        </div>

        {/* Featured Courses Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
                <HiOutlineAcademicCap className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Featured Courses</h3>
                <p className="text-gray-600">Comprehensive learning paths to advance your skills</p>
              </div>
            </div>
            <Link to="/selecole/course" className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center gap-1">
              View all <HiOutlineArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {loadingCourses
              ? Array.from({ length: 4 }).map((_, index) => <CourseSkeleton key={index} />)
              : courses.map((course) => <CourseCard key={course._id} course={course} onClick={() => navigate(`/selecole/course/${course._id}`)} />)}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════ ADMIN COMPONENTS ═══════════════════

function KPICard({ icon, label, value, subtitle, color, onClick }) {
  const colorMap = {
    blue: "bg-blue-600 text-white",
    green: "bg-emerald-600 text-white",
    purple: "bg-purple-600 text-white",
    orange: "bg-orange-500 text-white",
  };

  return (
    <div
      onClick={onClick}
      className={`${colorMap[color]} rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 ${onClick ? "cursor-pointer" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">{icon}</div>
        {subtitle && <span className="text-xs opacity-80">{subtitle}</span>}
      </div>
      <div className="text-3xl font-bold mb-1">{value}</div>
      <div className="text-sm opacity-90">{label}</div>
    </div>
  );
}

function PipelineStage({ label, count, color, lightColor, total, users, to }) {
  const navigate = useNavigate();
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

  return (
    <div className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors" onClick={() => to && navigate(to)}>
      <div className="w-24 flex-shrink-0">
        <span className={`text-xs font-medium px-2 py-1 rounded-full ${lightColor}`}>{label}</span>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div className={`${color} h-full rounded-full transition-all duration-500`} style={{ width: `${Math.max(percentage, 2)}%` }} />
          </div>
          <span className="text-sm font-bold text-gray-900 w-6 text-right">{count}</span>
        </div>
      </div>
      <div className="flex -space-x-1.5 flex-shrink-0">
        {users.map((u) => (
          <img key={u._id} src={u.avatar} alt={u.name} className="w-6 h-6 rounded-full border-2 border-white object-cover" title={u.name} />
        ))}
        {count > 4 && <span className="w-6 h-6 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-[10px] font-medium text-gray-600">+{count - 4}</span>}
      </div>
    </div>
  );
}

// ═══════════════════ USER COMPONENTS ═══════════════════

function StatCard({ icon, title, value, subtitle, color, progress, onClick }) {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-600 border-blue-200",
    purple: "bg-purple-100 text-purple-600 border-purple-200",
    green: "bg-green-100 text-green-600 border-green-200",
    orange: "bg-orange-100 text-orange-600 border-orange-200",
  };

  const bgClasses = {
    blue: "bg-blue-50",
    purple: "bg-purple-50",
    green: "bg-green-50",
    orange: "bg-orange-50",
  };

  return (
    <div onClick={onClick} className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 ${onClick ? "cursor-pointer" : ""}`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>{icon}</div>
        {progress !== undefined && <div className="text-xs font-medium text-gray-500">{progress}%</div>}
      </div>
      <div className="space-y-1">
        <div className="text-sm font-medium text-gray-600">{title}</div>
        <div className="text-3xl font-bold text-gray-900">{value}</div>
        <div className="text-xs text-gray-500">{subtitle}</div>
      </div>
      {progress !== undefined && (
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className={`h-2 rounded-full transition-all duration-500 ${bgClasses[color]}`} style={{ width: `${Math.min(progress, 100)}%` }}></div>
          </div>
        </div>
      )}
    </div>
  );
}

function QuickActionsCard() {
  const navigate = useNavigate();

  const actions = [
    { label: "Browse Workshops", path: "/selecole/learn", icon: <HiOutlineBookOpen className="w-5 h-5" />, bgColor: "bg-purple-100", textColor: "text-purple-600" },
    { label: "Explore Courses", path: "/selecole/course", icon: <HiOutlineAcademicCap className="w-5 h-5" />, bgColor: "bg-blue-100", textColor: "text-blue-600" },
    { label: "Career Paths", path: "/selecole/career-path", icon: <HiOutlineSparkles className="w-5 h-5" />, bgColor: "bg-green-100", textColor: "text-green-600" },
    { label: "View Stats", path: "/selecole/stats", icon: <HiOutlineChartBar className="w-5 h-5" />, bgColor: "bg-orange-100", textColor: "text-orange-600" },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
      <div className="space-y-2">
        {actions.map((action) => (
          <button
            key={action.path}
            onClick={() => navigate(action.path)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left hover:bg-gray-50 transition-colors duration-200 group">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${action.bgColor} ${action.textColor}`}>{action.icon}</div>
            <span className="font-medium text-gray-700 group-hover:text-gray-900">{action.label}</span>
            <HiOutlineArrowRight className="w-4 h-4 ml-auto text-gray-400 group-hover:text-gray-600" />
          </button>
        ))}
      </div>
    </div>
  );
}

function ResourcesCard() {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
          <HiOutlineDocumentText className="w-5 h-5 text-blue-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900">Resources</h3>
      </div>
      <p className="text-sm text-gray-600 mb-6 leading-relaxed">Learn more about our mission, vision, and technology.</p>
      <a
        href="https://github.com/selego/whitepaper"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 w-full justify-center">
        <HiOutlineArrowTopRightOnSquare className="w-4 h-4" />
        Read Whitepaper
      </a>
    </div>
  );
}

function WorkshopCard({ workshop, onClick, compact = false }) {
  return (
    <div
      onClick={onClick}
      className="group bg-gray-50 hover:bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-300 cursor-pointer overflow-hidden transform hover:-translate-y-1">
      <div className={`relative w-full ${compact ? "h-[140px]" : "h-[160px]"} overflow-hidden`}>
        <img
          src={workshop.image || placeholder}
          alt={workshop.image ? workshop.name : "Workshop placeholder"}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        {workshop.category && (
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/90 backdrop-blur-sm text-gray-700 shadow-sm">{workshop.category}</span>
          </div>
        )}
      </div>
      <div className={`${compact ? "p-4" : "p-5"}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700">Workshop</span>
        </div>
        <h4 className={`font-semibold text-gray-900 group-hover:text-purple-700 transition-colors duration-200 line-clamp-2 mb-2 ${compact ? "text-sm" : ""}`}>{workshop.name}</h4>
        {workshop.description && !compact && <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed mb-3">{workshop.description}</p>}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="text-xs text-gray-500">{workshop.updated_at ? new Date(workshop.updated_at).toLocaleDateString() : "Recently added"}</div>
          <div className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">Start →</div>
        </div>
      </div>
    </div>
  );
}

function CourseCard({ course, onClick }) {
  return (
    <div
      onClick={onClick}
      className="group bg-gray-50 hover:bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-gray-300 transition-all duration-300 cursor-pointer overflow-hidden transform hover:-translate-y-1 flex flex-col">
      <div className="relative w-full h-[180px] overflow-hidden">
        <img
          src={course.image || placeholder}
          alt={course.image ? course.title : "Course placeholder"}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        {course.category && (
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/90 backdrop-blur-sm text-gray-700 shadow-sm">{course.category}</span>
          </div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">Course</span>
        </div>
        <h4 className="text-lg font-semibold mb-2 text-gray-900 group-hover:text-blue-700 transition-colors duration-200 line-clamp-2">{course.title}</h4>
        {course.description && <p className="text-sm text-gray-600 mb-4 line-clamp-3 leading-relaxed flex-1">{course.description}</p>}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
          <div className="flex items-center text-xs text-gray-500">
            <HiOutlineDocumentText className="w-4 h-4 mr-1.5 text-blue-500" />
            <span className="font-medium">{course.updatedAt ? new Date(course.updatedAt).toLocaleDateString() : "Recently added"}</span>
          </div>
          <div className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">Start →</div>
        </div>
      </div>
    </div>
  );
}

const WorkshopSkeleton = () => (
  <div className="bg-gray-50 rounded-2xl shadow-sm border border-gray-200 overflow-hidden animate-pulse">
    <div className="w-full h-[160px] bg-gray-200"></div>
    <div className="p-4">
      <div className="w-16 h-5 bg-gray-200 rounded-md mb-2"></div>
      <div className="w-full h-5 bg-gray-200 rounded mb-2"></div>
      <div className="w-3/4 h-4 bg-gray-200 rounded mb-3"></div>
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="w-20 h-3 bg-gray-200 rounded"></div>
        <div className="w-24 h-3 bg-gray-200 rounded"></div>
      </div>
    </div>
  </div>
);

const CourseSkeleton = () => (
  <div className="bg-gray-50 rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col animate-pulse">
    <div className="w-full h-[180px] bg-gray-200"></div>
    <div className="p-5 flex-1 flex flex-col">
      <div className="w-12 h-5 bg-gray-200 rounded-md mb-3"></div>
      <div className="w-full h-6 bg-gray-200 rounded mb-2"></div>
      <div className="w-4/5 h-4 bg-gray-200 rounded mb-2"></div>
      <div className="w-3/5 h-4 bg-gray-200 rounded mb-4"></div>
      <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
        <div className="w-24 h-3 bg-gray-200 rounded"></div>
        <div className="w-20 h-3 bg-gray-200 rounded"></div>
      </div>
    </div>
  </div>
);

export default Home;
